import type { MouseEvent, PropsWithChildren } from 'react';

import { cn } from '../../utils/style-utils';
import UiButton from './ui-button';

export type ModalAction = {
  id: string;
  variant?: 'primary' | 'secondary';
  label: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>, modalId?: string) => void;
  disabled?: boolean;
};

type BaseModalProps = {
  title?: string;
  actions?: ModalAction[];
  headerClose?: boolean;
  topAccentClassName?: string;
  actionsClassName?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onClose: () => void;
} & PropsWithChildren;

const BaseModal: React.FC<BaseModalProps> = ({
  title,
  children,
  actions,
  headerClose = true,
  topAccentClassName,
  actionsClassName,
  className,
  onClose,
  size,
}) => {
  const handleClose = () => {
    onClose();
  };

  const showHeader = !!title || headerClose;

  const modalClassName = className
    ? className
    : 'fixed top-1/6 left-1/2 z-[301] w-11/12 max-w-md -translate-x-1/2 rounded-lg bg-white sm:w-auto sm:min-w-lg';

  return (
    <>
      <div className="fixed inset-0 z-300 bg-black/60" />
      <div
        className={cn([
          modalClassName,
          size === 'lg' && 'top-1/2 w-lg max-w-full -translate-y-1/2',
          size === 'xl' && 'top-1/2 w-xl max-w-full -translate-y-1/2',
        ])}
      >
        {topAccentClassName && (
          <div className={`h-1 rounded-t-lg ${topAccentClassName}`} />
        )}
        {showHeader && (
          <div className="flex items-center justify-end border-b border-slate-200 px-4 py-2 text-lg">
            {title && <h3 className="flex-1 font-semibold">{title}</h3>}
            {headerClose && (
              <button
                className="ml-4 text-2xl font-semibold text-neutral-500 hover:text-neutral-700"
                onClick={handleClose}
              >
                &times;
              </button>
            )}
          </div>
        )}
        <div className="p-4">{children}</div>
        {actions && actions.length && (
          <div className={cn('flex justify-end gap-x-4 p-4', actionsClassName)}>
            {actions.map(action => (
              <UiButton
                key={action.id}
                variant={
                  action.variant === 'secondary' ? 'ghost' : action.variant
                }
                size="sm"
                onClick={action.onClick}
                disabled={action.disabled}
                className="rounded-full"
              >
                {action.label}
              </UiButton>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default BaseModal;
